/**
 * User preferences — single source of truth for theme, notifications,
 * avatar, and other per-user settings.
 *
 * Storage strategy:
 *   1. **localStorage** is the fast path — read on every render, no network.
 *   2. **RTDB** under `users/{uid}/preferences/*` is the source of truth
 *      across devices. We sync localStorage → RTDB on every save, and
 *      RTDB → localStorage on auth-ready so a re-install or another
 *      device picks up the same preferences.
 *
 * Default values are defined per-field. The settings UI uses the same
 * defaults so the toggles always have a value to show.
 */

import { auth, ref, ensureAuthReady } from '../../core/firebase.js';
import * as bus from '../../core/eventBus.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('settings');

/** localStorage key — single JSON blob for all preferences. */
const LS_KEY = 'c2c_preferences';

/** @typedef {'light' | 'dark' | 'auto'} ThemeMode */

/** @typedef {Object} NotifPrefs
 *  @property {boolean} licenseExpiry  — license-renewal reminders
 *  @property {boolean} recalls        — manufacturer recall alerts
 *  @property {boolean} ownership      — ownership-change alerts
 *  @property {boolean} chat           — incoming messages from other drivers
 *  @property {boolean} weather        — extreme-weather warnings
 *  @property {boolean} news           — weekly news digest
 */

/** @typedef {Object} QuietHours
 *  @property {boolean} enabled
 *  @property {string} from   — `HH:MM`
 *  @property {string} to     — `HH:MM`
 */

/** @typedef {Object} Preferences
 *  @property {ThemeMode} theme
 *  @property {string} avatar     — emoji, e.g. "🚗"
 *  @property {NotifPrefs} notif
 *  @property {QuietHours} quiet
 *  @property {string} language   — currently only 'he' (reserved for future i18n)
 */

/** @type {Preferences} */
const DEFAULTS = {
  theme: 'auto',
  avatar: '🚗',
  notif: {
    licenseExpiry: true,
    recalls: true,
    ownership: true,
    chat: true,
    weather: true,
    news: true,
  },
  quiet: {
    enabled: false,
    from: '22:00',
    to: '07:00',
  },
  language: 'he',
};

let initialised = false;

/** @returns {Preferences} */
function readLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return clone(DEFAULTS);
    const parsed = JSON.parse(raw);
    // Merge with defaults so adding a new field in the future doesn't
    // leave existing users with `undefined`.
    return {
      ...DEFAULTS,
      ...parsed,
      notif: { ...DEFAULTS.notif, ...(parsed?.notif || {}) },
      quiet: { ...DEFAULTS.quiet, ...(parsed?.quiet || {}) },
    };
  } catch (_) { return clone(DEFAULTS); }
}

function writeLocal(prefs) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(prefs)); }
  catch (_) { /* quota / disabled — non-fatal */ }
}

function clone(o) { return JSON.parse(JSON.stringify(o)); }

/**
 * Public read — synchronous, hits localStorage.
 * @returns {Preferences}
 */
export function get() {
  return readLocal();
}

/**
 * Save partial preferences. Merges with the current set and persists to
 * both localStorage AND (best-effort) RTDB. Emits `settings:changed`.
 *
 * @param {Partial<Preferences>} patch
 * @returns {Promise<Preferences>}
 */
export async function update(patch) {
  const current = readLocal();
  const next = {
    ...current,
    ...patch,
    notif: { ...current.notif, ...(patch.notif || {}) },
    quiet: { ...current.quiet, ...(patch.quiet || {}) },
  };
  writeLocal(next);
  applyTheme(next.theme);
  bus.emit('settings:changed', next);
  // Fire-and-forget RTDB write so the call returns fast.
  void syncToRTDB(next).catch((e) => log.warn('RTDB sync failed:', e?.message));
  return next;
}

async function syncToRTDB(prefs) {
  const uid = auth().currentUser?.uid;
  if (!uid) return;
  await ref(`users/${uid}/preferences`).set(prefs);
}

/**
 * Apply the theme by toggling `data-theme` on `<html>`. CSS variables in
 * `tokens.css` flip surfaces / ink for `data-theme="dark"` and (when the
 * OS is dark) for `data-theme="auto"`.
 *
 * @param {ThemeMode} mode
 */
export function applyTheme(mode) {
  try {
    document.documentElement.setAttribute('data-theme', mode);
  } catch (_) { /* ignore */ }
}

/**
 * Initialise on bootstrap. Applies the current theme immediately, then
 * once auth resolves, pulls server-side preferences (if any) so a
 * second device sees the same settings.
 */
export function init() {
  if (initialised) return;
  initialised = true;
  applyTheme(readLocal().theme);

  void ensureAuthReady().then(async () => {
    const uid = auth().currentUser?.uid;
    if (!uid) return;
    try {
      const snap = await ref(`users/${uid}/preferences`).once('value');
      const server = snap.val();
      if (server && typeof server === 'object') {
        // Merge defaults <- local <- server. Server wins for fields it has.
        const local = readLocal();
        const merged = {
          ...DEFAULTS,
          ...local,
          ...server,
          notif: { ...DEFAULTS.notif, ...(local.notif || {}), ...(server.notif || {}) },
          quiet: { ...DEFAULTS.quiet, ...(local.quiet || {}), ...(server.quiet || {}) },
        };
        writeLocal(merged);
        applyTheme(merged.theme);
        bus.emit('settings:changed', merged);
      }
    } catch (err) {
      log.warn('preferences fetch failed:', err?.message);
    }
  });
}

export { DEFAULTS };
